import React, { useEffect } from 'react';
import {
  Button,
  Copy,
  Heading,
  Stack,
  SvgBriefcase,
  SvgCaretDown,
  SvgCaretUp,
  SvgCircleCheck,
  Flex,
  Square,
  SvgX,
} from '@grubhubprod/gh-cookbook-react';
import { Link } from '@grubhubprod/gh-cookbook-react/dist';
import { Dropdown } from 'src/components/Dropdown/Dropdown';
import {
  AdminCorpsBySignupType,
  BASIC_ACCOUNT_STATUS,
  CORP_SIGN_UP_TYPE,
  ICorpByRole,
} from 'src/components/AuthProvider/AuthProvider';
import { getWindow } from 'src/utilities/window';
import { useAnalytics } from 'src/components/AnalyticsProvider/AnalyticsProvider';
import { MOBILE_NAV_HEIGHT } from 'src/components/Navigation/NavBar';
import { StyledDropdownSquare } from 'src/utilities/sharedStyledComponents';
import { ROLES } from 'src/types/Employee';
import { ANALYTICS_TIMEOUT } from 'src/components/AnalyticsProvider/useSendAnalytics';
import { getHomeUrl } from 'src/constants/urls';

export interface IMultiCorpDropdownProps {
  hasPremiumCorps: boolean;
  corpId: string;
  hasBasicCorps: boolean;
  allUserCorps: AdminCorpsBySignupType;
  isMobile?: boolean;
  openDropdownCb?: (isOpen: boolean) => void;
}

export function MultiCorpDropdown({
  hasPremiumCorps,
  hasBasicCorps,
  allUserCorps,
  corpId,
  isMobile,
  openDropdownCb,
}: IMultiCorpDropdownProps): JSX.Element {
  return (
    <Dropdown
      onToggle={(isOpen) => openDropdownCb && openDropdownCb(isOpen)}
      isMenuRightAligned
      isFullWidth={isMobile}
      closeMenuOnClick={false}
      toggle={({ expanded }) => (
        <Button block variant="tertiary" buttonSize="small">
          <Stack as="span" x="xxx-sm">
            {isMobile && expanded ? (
              <SvgX
                size="24px"
                pointerEvents="none"
                aria-label="corps toggle x"
              />
            ) : (
              <SvgBriefcase
                size="24px"
                pointerEvents="none"
                aria-label="corps toggle"
              />
            )}
          </Stack>
          {!isMobile && (
            <>
              {expanded ? (
                <SvgCaretUp size="16px" pointerEvents="none" />
              ) : (
                <SvgCaretDown size="16px" pointerEvents="none" />
              )}
            </>
          )}
        </Button>
      )}
      menu={({ show }) =>
        show && (
          <MultiCorpDropdownMenu
            hasPremiumCorps={hasPremiumCorps}
            hasBasicCorps={hasBasicCorps}
            allUserCorps={allUserCorps}
            corpId={corpId}
            isMobile={isMobile}
          />
        )
      }
    />
  );
}

export interface IMultiCorpDropdownMenuProps {
  corpId: string;
  hasPremiumCorps: boolean;
  hasBasicCorps: boolean;
  allUserCorps: AdminCorpsBySignupType;
  isMobile?: boolean;
}

export function MultiCorpDropdownMenu({
  corpId,
  allUserCorps,
  hasPremiumCorps,
  hasBasicCorps,
  isMobile,
}: IMultiCorpDropdownMenuProps): JSX.Element {
  const { sendTealiumEvent } = useAnalytics();
  useEffect(() => {
    sendAccountSwitchAnalytics(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function sendAccountSwitchAnalytics(
    hasChangedAccounts: boolean,
    signupType?: CORP_SIGN_UP_TYPE
  ) {
    sendTealiumEvent({
      eventcategory: 'admin account settings',
      eventaction: 'client selector_cta',
      eventlabel: hasChangedAccounts
        ? getAnalyticsEventLabel(signupType)
        : 'see list of client accounts',
    });
  }

  function getAnalyticsEventLabel(signupType?: CORP_SIGN_UP_TYPE): string {
    switch (signupType) {
      case CORP_SIGN_UP_TYPE.PREMIUM: {
        return 'switch client account to_enterprise account';
      }
      case CORP_SIGN_UP_TYPE.BASIC: {
        return 'switch client account to_self-service account';
      }
      default: {
        return '';
      }
    }
  }

  return (
    <Stack
      right="0"
      width={isMobile ? '100vw' : '276px'}
      top={isMobile ? MOBILE_NAV_HEIGHT : '0'}
      height={isMobile ? `calc(100% - ${MOBILE_NAV_HEIGHT})` : 'auto'}
      position={isMobile ? 'fixed' : 'relative'}
      dimension="1"
      zIndex={10}
      bg="globals.white"
      borderRadius={isMobile ? '0' : '4px'}
      overflow="auto"
    >
      {hasPremiumCorps && (
        <MultiCorpDropdownSectionItem
          heading="Enterprise Accounts"
          corps={allUserCorps.PREMIUM}
          corpId={corpId}
          sendAccountSwitchAnalyticsCb={() =>
            sendAccountSwitchAnalytics(true, CORP_SIGN_UP_TYPE.PREMIUM)
          }
        />
      )}
      {hasBasicCorps && (
        <MultiCorpDropdownSectionItem
          heading="Self-Service Accounts"
          corps={allUserCorps.BASIC}
          corpId={corpId}
          sendAccountSwitchAnalyticsCb={() =>
            sendAccountSwitchAnalytics(true, CORP_SIGN_UP_TYPE.BASIC)
          }
        />
      )}
    </Stack>
  );
}

export interface IMultiCorpDropdownSectionProps {
  corpId: string;
  corps: ICorpByRole[];
  sendAccountSwitchAnalyticsCb: () => void;
  heading: string;
}

export function MultiCorpDropdownSectionItem({
  corpId,
  corps,
  sendAccountSwitchAnalyticsCb,
  heading,
}: IMultiCorpDropdownSectionProps): JSX.Element {
  function getCorpUrlBySignupType(
    corpId: string,
    signupType: CORP_SIGN_UP_TYPE,
    accountStatus?: BASIC_ACCOUNT_STATUS,
    role?: string
  ): string {
    const window = getWindow();
    // this will return the current path but with a corp ID param appended
    // which will reload the current page for the specified corp ID
    if (signupType === CORP_SIGN_UP_TYPE.PREMIUM) {
      if (role === ROLES.CORP_PARENT_ADMIN) {
        return `${window.location.origin}/corporate/enterprise/parents/${corpId}/children`;
      }
      return getHomeUrl(corpId);
    }

    if (signupType !== CORP_SIGN_UP_TYPE.BASIC) {
      return '';
    }

    if (accountStatus === BASIC_ACCOUNT_STATUS.SIGN_UP_IN_PROGRESS) {
      return `${window.location.origin}/corporate/signup/step/1`;
    }

    return `${window.location.origin}/corporate/management/${corpId}/home`;
  }

  return (
    <>
      <Square
        inset="md"
        style={{
          paddingBottom: 0,
        }}
      >
        <Stack y="xx-sm">
          <Heading variant="h6">{heading}</Heading>
        </Stack>
      </Square>
      {corps.map((corp) => (
        <React.Fragment key={corp.owner_id}>
          {corp.owner_id === corpId ? (
            <MultiCorpDropdownItem corp={corp} corpId={corpId} />
          ) : (
            <Link
              href={getCorpUrlBySignupType(
                corp.owner_id,
                corp.sign_up_type,
                corp.account_status,
                corp.role
              )}
              onClick={(event) => {
                // we need to send analytics before navigating to new path
                event.preventDefault();
                sendAccountSwitchAnalyticsCb();

                const anchorElement = event.currentTarget as HTMLAnchorElement;
                setTimeout(() => {
                  // Using this since we can navigate from a react-router OR nextjs managed page to react-router OR nextjs managed page
                  // We'll change this back to using push from react-router after we migrate enterprise routes
                  window.location.href = anchorElement.href;
                }, ANALYTICS_TIMEOUT);
              }}
            >
              <MultiCorpDropdownItem
                key={corp.owner_id}
                corp={corp}
                corpId={corpId}
              />
            </Link>
          )}
        </React.Fragment>
      ))}
    </>
  );
}

export interface IMultiCorpDropdownItemProps {
  corpId: string;
  corp: ICorpByRole;
}

export function MultiCorpDropdownItem({
  corpId,
  corp,
}: IMultiCorpDropdownItemProps): JSX.Element {
  const isBasicSignupInProgress =
    corp.sign_up_type === CORP_SIGN_UP_TYPE.BASIC &&
    corp.account_status === BASIC_ACCOUNT_STATUS.SIGN_UP_IN_PROGRESS;

  return (
    <StyledDropdownSquare
      inset="md"
      borderBottom="1px solid"
      borderColor="lightLine"
    >
      <Flex justifyContent="space-between" alignItems="center">
        <Stack x="xx-sm">
          <div className="ellipsisOverflow">
            <Copy variant="body" color="primaryText">
              {corp.owner_name}
            </Copy>
          </div>
          {corp.client_id && (
            <Copy variant="body" color="secondaryText">
              {corp.client_id}
            </Copy>
          )}
          {isBasicSignupInProgress && (
            <Copy variant="caption" color="warning" display="block">
              Finish account setup
            </Copy>
          )}
        </Stack>
        {corp.owner_id === corpId && (
          <Copy variant="body" color="interactive">
            <SvgCircleCheck size="24px" />
          </Copy>
        )}
      </Flex>
    </StyledDropdownSquare>
  );
}
